/**
 * Unified API Service for Booker App
 * Implements the two-API architecture:
 * - LOCAL API: Booker-specific features (bookings, appointments, calendar)
 * - CENTRAL API: Shared services (auth, payments, OAuth integrations)
 */

import axios from 'axios';
import { auth } from '../firebase/config';
import { dispatch403Toast } from '../utils/apiClient';
import { impersonationState } from './impersonationState';

class ApiService {
  constructor() {
    // SECURE API - for app-specific features (bookings, calendar, etc.)
    // Pattern A: All local features go through secureApi
    const secureApiUrl = process.env.REACT_APP_SECURE_API_URL;
    if (!secureApiUrl) {
      throw new Error('REACT_APP_SECURE_API_URL is not configured. Add it to your .env.local file.');
    }
    this.localAPI = axios.create({
      baseURL: secureApiUrl,
      headers: { 'Content-Type': 'application/json' }
    });

    // CENTRAL API - for shared services (payments, auth, OAuth)
    const centralApiUrl = process.env.REACT_APP_CENTRAL_API_URL;
    if (!centralApiUrl) {
      throw new Error('REACT_APP_CENTRAL_API_URL is not configured. Add it to your .env.local file.');
    }
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    this.centralAPI = axios.create({
      baseURL: centralApiUrl,
      headers: {
        'Content-Type': 'application/json',
        'x-app-id': appId  // Required by Central API for multi-app routing
      }
    });

    // Setup auth interceptor for BOTH APIs
    [this.localAPI, this.centralAPI].forEach(api => {
      api.interceptors.request.use(async (config) => {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        }

        // ✅ CRITICAL FIX: Add impersonation headers if support is impersonating
        // BUT skip for privileged routes (/support, /admin)
        const isPrivilegedRoute =
          config.url?.includes('/support/') || config.url?.endsWith('/support') ||
          config.url?.includes('/admin/') || config.url?.endsWith('/admin');
        if (!isPrivilegedRoute) {
          const impersonation = impersonationState.get();
          if (impersonation && impersonation.isImpersonating) {
            config.headers['X-Impersonate-User'] = impersonation.targetUserId;
            config.headers['X-Impersonation-Session'] = impersonation.sessionId;
            console.log('🔐 Axios: Adding impersonation headers', {
              url: config.url,
              targetUserId: impersonation.targetUserId,
              sessionId: impersonation.sessionId
            });
          }
        } else {
          console.log('⚠️ Axios: Skipping impersonation headers for privileged route:', config.url);
        }

        return config;
      });

      // Response interceptor for error handling with subscription toast support
      api.interceptors.response.use(
        response => response,
        error => {
          console.error('API Error:', error.response?.data || error.message);

          // Handle 403 subscription/paywall errors
          if (error.response?.status === 403) {
            const errorData = error.response.data;

            // Check if caller wants to suppress subscription toasts (e.g., during onboarding)
            const suppressToast = error.config?.suppressSubscriptionToast;

            if (!suppressToast) {
              dispatch403Toast(errorData);
            }

            // Mark error as subscription-related
            error.isSubscriptionError = true;
          }

          return Promise.reject(error);
        }
      );
    });
  }

  // ═══════════════════════════════════════════════════════════
  // GENERIC HTTP METHODS (for use by other services)
  // ═══════════════════════════════════════════════════════════

  /**
   * Get current auth token
   */
  async getAuthToken() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    return await user.getIdToken();
  }

  /**
   * Generic GET request to local API
   */
  async get(endpoint, config = {}) {
    const response = await this.localAPI.get(endpoint, config);
    return response.data;
  }

  /**
   * Generic POST request to local API
   */
  async post(endpoint, data, config = {}) {
    const response = await this.localAPI.post(endpoint, data, config);
    return response.data;
  }

  /**
   * Generic PUT request to local API
   */
  async put(endpoint, data, config = {}) {
    const response = await this.localAPI.put(endpoint, data, config);
    return response.data;
  }

  /**
   * Generic DELETE request to local API
   */
  async delete(endpoint, config = {}) {
    const response = await this.localAPI.delete(endpoint, config);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // LOCAL API METHODS (App-specific - Bookings)
  // ═══════════════════════════════════════════════════════════

  /**
   * Create a new booking
   *
   * ⚠️ LEGACY API: Use BookingApiService.createAppointment() for new code
   *
   * Note: Quota enforcement happens on backend. If you need client-side
   * preflight checks, use useSubscriptionCheck().checkLimit('maxAppointments', count)
   * before calling this method to avoid wasted 403 calls.
   */
  async createBooking(bookingData) {
    const response = await this.localAPI.post('/bookings', bookingData);
    return response.data;
  }

  /**
   * Get all bookings for current user
   */
  async getBookings() {
    const response = await this.localAPI.get('/bookings');
    return response.data;
  }

  /**
   * Update booking
   */
  async updateBooking(bookingId, updates) {
    const response = await this.localAPI.put(`/bookings/${bookingId}`, updates);
    return response.data;
  }

  /**
   * Cancel booking
   */
  async cancelBooking(bookingId) {
    const response = await this.localAPI.delete(`/bookings/${bookingId}`);
    return response.data;
  }

  /**
   * Get availability for a date
   */
  async getAvailability(date, serviceId) {
    const response = await this.localAPI.get('/availability', {
      params: { date, serviceId }
    });
    return response.data;
  }

  /**
   * Sync with Google Calendar
   */
  async syncGoogleCalendar() {
    const response = await this.localAPI.post('/calendar/sync');
    return response.data;
  }

  /**
   * Get services
   */
  async getServices() {
    const response = await this.localAPI.get('/services');
    return response.data;
  }

  /**
   * Create service
   */
  async createService(serviceData) {
    const response = await this.localAPI.post('/services', serviceData);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // CENTRAL API METHODS (Shared services)
  // ═══════════════════════════════════════════════════════════

  /**
   * Get pricing plans for booker app
   */
  async getPricing() {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.get(`/payments/plans?appId=${appId}`);
    return response.data;
  }

  /**
   * Create Stripe checkout session for subscription
   */
  async createSubscription(planId, priceId, interval = 'monthly') {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post('/subscriptions/create-checkout', {
      appId,
      planId,
      priceId,
      interval,
      successUrl: `${window.location.origin}/subscription-success`,
      cancelUrl: `${window.location.origin}/pricing`
    });
    return response.data;
  }

  /**
   * Get all user subscriptions across all apps
   */
  async getMySubscriptions() {
    const response = await this.centralAPI.get('/subscriptions/all');
    return response.data;
  }

  /**
   * Get subscription status for booker app
   */
  async getSubscriptionStatus() {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.get(`/subscriptions/status/${appId}`);
    return response.data;
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(subscriptionId) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post(`/subscriptions/cancel/${appId}`, {
      subscriptionId
    });
    return response.data;
  }

  /**
   * Pause subscription
   */
  async pauseSubscription(subscriptionId) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post(`/subscriptions/pause/${appId}`, {
      subscriptionId
    });
    return response.data;
  }

  /**
   * Resume subscription
   */
  async resumeSubscription(subscriptionId) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post(`/subscriptions/resume/${appId}`, {
      subscriptionId
    });
    return response.data;
  }

  /**
   * Get Stripe billing portal URL
   */
  async getBillingPortalUrl() {
    const response = await this.centralAPI.post('/subscriptions/billing-portal', {
      returnUrl: window.location.origin
    });
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // OAUTH INTEGRATIONS (Central API)
  // ═══════════════════════════════════════════════════════════

  /**
   * Initiate OAuth flow
   * @param {string} provider - 'google', 'microsoft', 'quickbooks', etc.
   */
  async connectOAuth(provider) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post(`/integrations/oauth/${provider}/initiate`, {
      appId,
      returnPath: '/settings?tab=integrations',  // Return to integrations tab after OAuth
      // Central API will construct full URL: https://booker-1d469.web.app/settings?tab=integrations
    });
    return response.data;
  }

  /**
   * Get all connected OAuth integrations
   */
  async getMyIntegrations() {
    const response = await this.centralAPI.get('/integrations/connections');
    return response.data;
  }

  /**
   * Disconnect OAuth provider
   */
  async disconnectOAuth(provider) {
    const response = await this.centralAPI.delete(`/integrations/connections/${provider}`, {
      headers: { 'X-Confirm-Disconnect': 'user-initiated' }
    });
    return response.data;
  }

  /**
   * Test all OAuth credentials (admin only)
   */
  async testAllCredentials() {
    const response = await this.centralAPI.post('/integrations/system/test-all');
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // PAYMENTS (Central API)
  // ═══════════════════════════════════════════════════════════

  /**
   * Create payment intent for one-time payments via centralized API
   * @param {Object} options - Payment options
   * @param {number} options.amount - Amount in dollars (will be converted to cents)
   * @param {string} options.currency - Currency code (USD, CAD, etc.)
   * @param {string} options.description - Payment description
   * @param {string} options.recaptchaToken - reCAPTCHA token (required by central API)
   * @returns {Promise<{clientSecret: string, paymentIntentId: string, transactionId: string}>}
   */
  async createPaymentIntent({ amount, currency = 'USD', description, recaptchaToken }) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';

    if (!recaptchaToken) {
      throw new Error('reCAPTCHA token is required for payment intent creation');
    }

    const response = await this.centralAPI.post('/payments/stripe/create-payment-intent', {
      amount,  // Central API expects dollars, converts to cents internally
      currency,
      appId,
      description,
      recaptchaToken
    });

    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // CALENDAR (Central API - OAuth required)
  // ═══════════════════════════════════════════════════════════

  /**
   * Create calendar event via centralized OAuth integration
   * @param {Object} eventData - Event details
   * @returns {Promise<Object>} Created event data
   */
  async createCalendarEvent(eventData) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post('/integrations/calendar/create-event', {
      appId,
      ...eventData
    });
    return response.data;
  }

  /**
   * Get calendar events
   * @param {Object} options - Query options
   * @returns {Promise<Array>} List of events
   */
  async getCalendarEvents(options = {}) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.get('/integrations/calendar/events', {
      params: { appId, ...options }
    });
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // EMAIL (Central API - OAuth required)
  // ═══════════════════════════════════════════════════════════

  /**
   * Send email via centralized OAuth integration
   * @param {Object} emailData - Email details
   * @returns {Promise<Object>} Send result
   */
  async sendEmail(emailData) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post('/crm/email/send', {
      appId,
      ...emailData
    });
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // STRIPE CONNECT (Central API)
  // ═══════════════════════════════════════════════════════════

  /**
   * Get Stripe Connect status for current app
   * @returns {Promise<Object>} Connection status
   */
  async getStripeConnectStatus() {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.get('/integrations/stripe-connect/status', {
      params: { appId }
    });
    return response.data;
  }

  /**
   * Initiate Stripe Connect OAuth flow
   * @param {string} returnUrl - Where to redirect after connection
   * @returns {Promise<Object>} { success, data: { authUrl, accountId } }
   */
  async initiateStripeConnect(returnUrl) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post('/integrations/stripe-connect/initiate', {
      appId,
      returnUrl: returnUrl || window.location.href
    });
    return response.data;
  }

  /**
   * Enable/disable Stripe Connect for current app
   * @param {boolean} enabled - Whether to enable or disable
   * @returns {Promise<Object>} Success status
   */
  async toggleStripeConnectForApp(enabled) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.put('/integrations/stripe-connect/toggle-app', {
      appId,
      enabled
    });
    return response.data;
  }

  /**
   * Disconnect Stripe account (removes from ALL apps)
   * @returns {Promise<Object>} Success status
   */
  async disconnectStripeConnect() {
    const response = await this.centralAPI.delete('/integrations/stripe-connect/disconnect');
    return response.data;
  }

  /**
   * Create payment link for invoice/booking
   * @param {Object} options - Payment link options
   * @param {number} options.amount - Amount in dollars (will be converted to cents)
   * @param {string} options.currency - Currency code (default: 'usd')
   * @param {string} options.description - Payment description
   * @param {Object} options.metadata - Additional metadata
   * @param {string} options.returnUrl - Where to redirect after payment
   * @returns {Promise<Object>} { success, data: { paymentUrl, paymentLinkId } }
   */
  async createStripePaymentLink({ amount, currency = 'usd', description, metadata = {}, returnUrl }) {
    const appId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post('/integrations/stripe-connect/create-payment-link', {
      appId,
      amount,
      currency,
      description,
      metadata,
      returnUrl
    });
    return response.data;
  }

  /**
   * Connect Stripe via API Key (alternative to Stripe Connect)
   * @param {string} apiKey - Stripe API secret key
   * @returns {Promise<Object>} Success status
   */
  async connectStripeApiKey(apiKey) {
    const response = await this.centralAPI.post('/payments/stripe/config', {
      apiKey
    });
    return response.data;
  }

  /**
   * Get Stripe connection status (API Key or Connect)
   * Returns combined status from both API Key and Stripe Connect
   * @returns {Promise<Object>} { isConnected, connectionType, ... }
   */
  async getStripeConnectionStatus() {
    try {
      // Check API Key connection
      const apiKeyResponse = await this.centralAPI.get('/payments/stripe/config');
      if (apiKeyResponse.data.configured) {
        return {
          isConnected: true,
          connectionType: 'api-key',
          mode: apiKeyResponse.data.mode,
          accountName: apiKeyResponse.data.accountName,
          apiKeyLast4: apiKeyResponse.data.apiKeyLast4,
          connectedAt: apiKeyResponse.data.connectedAt
        };
      }

      // Fall back to checking Stripe Connect
      const appId = process.env.REACT_APP_APP_ID || 'booking-system';
      const connectResponse = await this.centralAPI.get('/integrations/stripe-connect/status', {
        params: { appId }
      });

      if (connectResponse.data.isConnected) {
        return {
          isConnected: true,
          connectionType: 'stripe-connect',
          ...connectResponse.data
        };
      }

      return { isConnected: false };
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      return { isConnected: false };
    }
  }

  /**
   * Disconnect Stripe (API Key or Connect)
   * @returns {Promise<Object>} Success status
   */
  async disconnectStripe() {
    try {
      // Try to disconnect API Key first
      const apiKeyResponse = await this.centralAPI.delete('/payments/stripe/config');
      return apiKeyResponse.data;
    } catch (error) {
      // Fall back to disconnecting Stripe Connect
      const connectResponse = await this.centralAPI.delete('/integrations/stripe-connect/disconnect');
      return connectResponse.data;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // USER MANAGEMENT (Central API)
  // ═══════════════════════════════════════════════════════════

  /**
   * Get unified user profile
   */
  async getUserProfile(userId) {
    const response = await this.centralAPI.get(`/admin/unified-users/${userId}`);
    return response.data;
  }

  /**
   * Get current user's profile
   */
  async getMyProfile() {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    return this.getUserProfile(user.uid);
  }

  /**
   * Update user profile
   */
  async updateUserProfile(userId, updates) {
    const response = await this.centralAPI.put(`/admin/unified-users/${userId}`, updates);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // CROSS-APP FEATURES (Central API)
  // ═══════════════════════════════════════════════════════════

  /**
   * Get available cross-app features
   */
  async getCrossAppFeatures() {
    const response = await this.centralAPI.get('/cross-app/features');
    return response.data;
  }

  /**
   * Record a referral between apps
   */
  async recordReferral(toAppId) {
    const fromAppId = process.env.REACT_APP_APP_ID || 'booking-system';
    const response = await this.centralAPI.post('/cross-app/record-referral', {
      fromAppId,
      toAppId
    });
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // UTILITY METHODS
  // ═══════════════════════════════════════════════════════════

  /**
   * Health check for both APIs
   */
  async healthCheck() {
    const results = {
      local: { healthy: false, error: null },
      central: { healthy: false, error: null }
    };

    try {
      await this.localAPI.get('/health');
      results.local.healthy = true;
    } catch (error) {
      results.local.error = error.message;
    }

    try {
      await this.centralAPI.get('/health');
      results.central.healthy = true;
    } catch (error) {
      results.central.error = error.message;
    }

    return results;
  }

  /**
   * Report a setup step completion to the Central dashboard.
   * Fire-and-forget — never blocks the caller.
   */
  reportSetupStep(stepId) {
    this.centralAPI.post('/dashboard/setup-progress', {
      appId: process.env.REACT_APP_APP_ID || 'booking-system',
      stepId,
      completed: true
    }, { suppressSubscriptionToast: true }).catch(() => {});
  }

  /**
   * Get configuration info for debugging
   */
  getConfig() {
    return {
      localAPI: this.localAPI.defaults.baseURL,
      centralAPI: this.centralAPI.defaults.baseURL,
      appId: process.env.REACT_APP_APP_ID,
      environment: process.env.NODE_ENV
    };
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;

// Named exports for convenience
export const {
  // Local API
  createBooking,
  getBookings,
  updateBooking,
  cancelBooking,
  getAvailability,
  syncGoogleCalendar,
  getServices,
  createService,
  // Central API - Payments
  getPricing,
  createSubscription,
  getMySubscriptions,
  getSubscriptionStatus,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
  getBillingPortalUrl,
  createPaymentIntent,
  // Central API - OAuth
  connectOAuth,
  getMyIntegrations,
  disconnectOAuth,
  // Central API - Calendar
  createCalendarEvent,
  getCalendarEvents,
  // Central API - Email
  sendEmail,
  // Central API - Stripe Connect
  getStripeConnectStatus,
  initiateStripeConnect,
  toggleStripeConnectForApp,
  disconnectStripeConnect,
  createStripePaymentLink,
  // Central API - Stripe (API Key & Status)
  connectStripeApiKey,
  getStripeConnectionStatus,
  disconnectStripe,
  // Central API - Users
  getUserProfile,
  getMyProfile,
  updateUserProfile,
  // Central API - Cross-app
  getCrossAppFeatures,
  recordReferral,
  // Central API - Dashboard
  reportSetupStep,
  // Utilities
  healthCheck,
  getConfig
} = apiService;
